import React, { useEffect, useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, IconButton, Button, Autocomplete, TextField, Container } from '@mui/material';
import { ExpandMoreOutlined, Edit, Save, Close, LocalActivity, ConfirmationNumber } from '@mui/icons-material';
import { api } from '../../services/api';
import Loading from '../../components/Loading';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const ColaboradoresAccesos = () => {
  const param = useParams();
  const [allTarifas, setAllTarifas] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});
  const [editando, setEditando] = useState(null);
  const [tarifasEdit, setTarifasEdit] = useState([]);
  const [loading, setLoading] = useState(true);

  // busqueda por usuario (debounced: el input se actualiza al toque, la búsqueda real espera 400ms)
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const resStaff = await api.participaciones.getParticipacionesByEvento(param.id, searchTerm, 100, 1);
      // todos los roles excepto el 3 (cliente)
      const colaboradoresFiltrados = resStaff?.data?.participaciones?.filter(
        (p) => p.idrol !== 3
      );
      setColaboradores(colaboradoresFiltrados);

      const asignacionesPorUsuario = {};
      for (const colab of colaboradoresFiltrados) {
        const resTarifas = await api.accesos.getTarifasAccesos(param.id, colab.idusuario);
        asignacionesPorUsuario[colab.idusuario] = (resTarifas || []).map(a => a.idtarifa);
      }
      setAsignaciones(asignacionesPorUsuario);
      setLoading(false);
    };

    fetchData();
  }, [param.id, searchTerm]);

  useEffect(() => {
    const fetchAllTarifas = async () => {
      const res = await api.tarifas.getTarifaByEventoWithoutPagination(param.id);
      if (res.status === 'success') setAllTarifas(res.data?.tarifas ?? []);
    };
    fetchAllTarifas();
  }, [param.id]);

  const handleGuardar = async (colab) => {
    setLoading(true);
    const actuales = asignaciones[colab.idusuario] || [];
    const nuevas = tarifasEdit;

    for (const idtarifa of actuales) {
      if (!nuevas.includes(idtarifa)) {
        const asignacion = await api.accesos.getTarifasAccesos(param.id, colab.idusuario);
        const found = (asignacion || []).find(a => a.idtarifa === idtarifa);
        if (found) await api.accesos.eliminarTarifaColaborador(param.id, found.id);
      }
    }
    for (const idtarifa of nuevas) {
      if (!actuales.includes(idtarifa)) {
        await api.accesos.asignarTarifaColaborador(param.id, {
          idevento: Number(param.id),
          idusuario: colab.idusuario,
          idtarifa
        });
      }
    }
    toast.success("Tarifas actualizadas");
    setEditando(null);
    const resTarifas = await api.accesos.getTarifasAccesos(param.id, colab.idusuario);
    setAsignaciones(prev => ({ ...prev, [colab.idusuario]: (resTarifas || []).map(a => a.idtarifa) }));
    setLoading(false);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#5656ff", mb: 2 }}>
          Asignación de Tarifas por Usuario
        </Typography>
        <TextField
          size="small"
          placeholder="Buscar por nombre o email"
          value={searchInput}
          onChange={handleSearchChange}
          sx={{
            fontFamily: "Bricolage Grotesque",
            minWidth: "320px",
            mb: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#B2BAC2',
              },
              '&:hover fieldset': {
                borderColor: '#5656ff',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#5656ff',
              },
            }
          }}
        />
        {colaboradores?.length === 0 ? (
          <Typography sx={{ color: "#888", py: 4, textAlign: "center" }}>
            {searchTerm ? "No se encontraron usuarios que coincidan con la búsqueda" : "No hay usuarios asignados a este evento."}
          </Typography>
        ) : (
          colaboradores.map(colab => {
            const tarifasAsignadas = (asignaciones[colab.idusuario] || []).map(idtarifa =>
              allTarifas.find(t => Number(t.id) === Number(idtarifa))
            ).filter(Boolean);

            return (
              <Accordion
                key={colab.idusuario}
                expanded={editando === colab.idusuario}
                sx={{ mb: 1, boxShadow: 1, borderRadius: 1 }}
                elevation={0}
                disableGutters
              >
                <AccordionSummary
                  expandIcon={null}
                  sx={{
                    cursor: "default",
                    userSelect: "none",
                    background: "#ffff",
                    borderRadius: 1,
                    minHeight: 0,
                    padding: "10px 15px",
                    '& .MuiAccordionSummary-content': { margin: 0, alignItems: "center" }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                    <Box>
                      <Typography sx={{ color: "#0b0062", fontWeight: 600, fontSize: 16 }}>{colab.usuario?.nombre} {colab.usuario?.apellido}</Typography>
                      <Typography sx={{ color: "#666", fontSize: 14 }}>{colab.usuario?.usuario}</Typography>
                      <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {tarifasAsignadas.length === 0
                          ? <Chip label="Sin tarifas asignadas" size="small" />
                          : tarifasAsignadas.map(t => (
                            <Chip key={t.id} label={t.concepto} size="small" />
                          ))}
                      </Box>
                    </Box>
                    <Box>
                      {editando === colab.idusuario ? (
                        <>
                          <IconButton onClick={() => handleGuardar(colab)} color="primary"><Save sx={{ color: "#5656ff" }} /></IconButton>
                          <IconButton onClick={() => setEditando(null)} color="error"><Close /></IconButton>
                        </>
                      ) : (
                        <IconButton onClick={() => {
                          setEditando(colab.idusuario);
                          setTarifasEdit(asignaciones[colab.idusuario] || []);
                        }}><Edit /></IconButton>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ background: "#fff" }}>
                  {editando === colab.idusuario && (
                    <Box>
                      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<ConfirmationNumber />}
                          onClick={() => {
                            const tickets = allTarifas.filter(t => t.idtipo === 1);
                            setTarifasEdit([
                              ...new Set([...tarifasEdit, ...tickets.map(t => t.id)])
                            ]);
                          }}
                          disabled={!allTarifas || allTarifas.filter(t => t.idtipo === 1).length === 0}
                          sx={{
                            fontSize: "11px",
                            textTransform: "none",
                            color: "#5656ff",
                            border: "none",
                            '&:hover': {
                              borderColor: "#5656ff",
                              backgroundColor: "#f0f0ff"
                            }
                          }}
                        >
                          Tickets
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<LocalActivity />}
                          onClick={() => {
                            const vouchers = allTarifas.filter(t => t.idtipo === 2);
                            setTarifasEdit([
                              ...new Set([...tarifasEdit, ...vouchers.map(t => t.id)])
                            ]);
                          }}
                          disabled={!allTarifas || allTarifas.filter(t => t.idtipo === 2).length === 0}
                          sx={{
                            fontSize: "11px",
                            textTransform: "none",
                            color: "#ff6b35",
                            border: "none",
                            '&:hover': {
                              borderColor: "#ff6b35",
                              backgroundColor: "#fff5f2"
                            }
                          }}
                        >
                          Vouchers
                        </Button>
                      </Box>
                      <Autocomplete
                        multiple
                        options={allTarifas}
                        getOptionLabel={t => t.concepto}
                        value={allTarifas.filter(t => tarifasEdit.includes(t.id))}
                        onChange={(_, newValue) => setTarifasEdit(newValue.map(t => t.id))}
                        filterSelectedOptions
                        renderInput={params => (
                          <TextField {...params} label="Tarifas" placeholder="Buscar tarifa..." />
                        )}
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          }))}
      </Box>
    </Container>
  );
};

export default ColaboradoresAccesos;